import React, { useEffect, useState, useCallback } from 'react';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from 'react-redux';
import AddCard from '../../../../../Business/modules/Credits/components/AddCard/index';
import AddBalance from '../../../../../Business/modules/Credits/components/AddBalance/index';
import AddPromo from '../../../../../Business/modules/Credits/components/AddPromo/index';
import SetReload from '../../../../../Business/modules/Credits/components/SetReload/index';
import { get } from 'lodash';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  FormControl,
  FormHelperText,
  Card, CardActions, CardContent,
} from "@mui/material";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useSnackbar } from "notistack";
import cardImg from "./cards.png";
import api from 'utils/api'
import { getApiErrorMessage } from 'utils/apiErrors';
import { fetchBusiness } from 'modules/Business/actions';
import { fetchUser } from 'modules/User/actions';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { ConfirmationDialog } from 'components';
import { getBalanceDisplay } from 'utils/balanceColor';
import {
  canApplyPromoInOfferFlow,
  resolveMinimumLaunchBalance,
} from 'utils/businessBalance';
import { saveAutoReloadPreference } from 'utils/businessAutoReload';
import OfferSaveActions from '../OfferSaveActions';
import WizardBackButton from '../WizardBackButton';
import { paymentSchema } from './paymentValidation';
import AutoReloadButton from 'modules/Business/components/AutoReloadButton';
import AutoReloadPreference, {
  FORM_COLUMN_AUTO_RELOAD_SX,
  useAutoReloadPreference,
} from 'modules/Business/components/AutoReloadPreference';

const OfferPayment = ({ values, onComplete, back, setActiveStep, onSave, onUpdate, saveButtonLabel }) => {
  const { enqueueSnackbar } = useSnackbar();
  const stripe = useStripe();
  const elements = useElements();
  const [posting, setPosting] = useState(false);

    const dispatch = useDispatch();
    const [showCardSelection, setShowCardSelection] = useState(false);
    const [showBalancePopup, setShowBalancePopup] = useState(false);
    const [showReloadPopup, setShowReloadPopup] = useState(false);
    const [showPromoPopup, setShowPromoPopup] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
  
    const business = useSelector(state => state.business);
    const autoReloadPreference = useAutoReloadPreference(business);
    const handleEnterPromoClick = () => {
      if (!canApplyPromoInOfferFlow(business.accountBalance, business)) {
        const minimum = resolveMinimumLaunchBalance(business);
        enqueueSnackbar(
          `Please add at least $${minimum} to your account before using a promo code`,
          { variant: 'warning' }
        );
        return;
      }
      setShowPromoPopup(true);
    };
    const userCards = useSelector(state => get(state, 'user.details.cards', []));
    
    const handleDeleteCard = useCallback((cardId) => {
      api
        .delete(`/stripe/customer/card/${cardId}`)
        .then(() => {
          setConfirmDelete(false);
          enqueueSnackbar('Card deleted', { variant: 'success' });
          dispatch(fetchBusiness(business.businessId, true));
          dispatch(fetchUser());
        })
        .catch((error) => {
          enqueueSnackbar(getApiErrorMessage(error, 'Couldn’t delete this card. Please try again.'), { variant: 'error' });
        });
    }, [business.businessId, dispatch, enqueueSnackbar]);
    const userHasCard = Array.isArray(userCards) && userCards.length > 0;
  
    useEffect(() => {
      dispatch(fetchBusiness(business.businessId, true));
    }, [dispatch, business.businessId]);


  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(paymentSchema),
    defaultValues: {
      amount: values.budget?.maxAmount || "",
      name: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
  });

  const handlePaymentSubmit = async (data) => {
    if (!stripe || !elements) {
      enqueueSnackbar("Stripe is not loaded. Please try again later.", { variant: "error" });
      return;
    }
  
    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      enqueueSnackbar("Card details are missing.", { variant: "error" });
      return;
    }
  
    const payload = {
      name: data.name,
      address_line1: data.address,
      address_city: data.city,
      address_state: data.state,
      address_zip: data.zipCode,
      address_country: data.country,
    };
  
    try {
      setPosting(true);

      const res = await stripe.createToken(cardElement, payload);

      if (!res.token) {
        enqueueSnackbar(res.error?.message || "Check the card details and try again.", { variant: "error" });
        setPosting(false);
        return;
      }
      const customerResponse = await api.post("/stripe/customer", {
        token: res.token.id,
        businessId: business.businessId,
      });

      if (customerResponse?.paymentMethodId && business.businessId) {
        await api.put(
          `/business/${business.businessId}/card/${customerResponse.paymentMethodId}?isDefault=true`
        );
      }

      // Refresh user and business data
      await Promise.all([
        dispatch(fetchUser()),
        dispatch(fetchBusiness(business.businessId, true))
      ]);

      try {
        await saveAutoReloadPreference(
          business.businessId,
          autoReloadPreference.reloadAmount
        );
        await dispatch(fetchBusiness(business.businessId, true));
      } catch (e) {
        enqueueSnackbar(
          'Payment method added, but auto reload could not be updated.',
          { variant: 'warning' }
        );
      }

      await new Promise(resolve => setTimeout(resolve, 5000));

      const paymentMethod = {
        card: {
          id:res.token.id,
          brand: res.token.card.brand,
          last4: res.token.card.last4,
          expMonth: res.token.card.exp_month,
          expYear: res.token.card.exp_year
        }
      };
  
      onComplete({
        ...data,
        paymentUpdated: paymentMethod
      });
  
      enqueueSnackbar("Payment method added successfully!", { variant: "success" });
    } catch (err) {
      console.error("Error adding payment method:", err);
      enqueueSnackbar(getApiErrorMessage(err, "Couldn’t add the payment method. Please try again."), { variant: "error" });
    } finally {
      setPosting(false);
    }
  };

  const requiresNewPaymentMethod = !business.card && !userHasCard;
  const showsPaymentOptions = Boolean(business.card || userHasCard);
  const handlePaymentFormBack = showsPaymentOptions
    ? () => setShowPaymentForm(false)
    : back;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {(showPaymentForm || requiresNewPaymentMethod) && (
        <Box sx={{ order: 2, mt: showsPaymentOptions ? 3 : 0 }}>
          <form onSubmit={handleSubmit(handlePaymentSubmit)}>
      {/* Title */}
      <Typography variant="h6" gutterBottom>
        Add new payment method
      </Typography>
      <AutoReloadPreference
        preference={autoReloadPreference}
        sx={{ ...FORM_COLUMN_AUTO_RELOAD_SX, mb: 3 }}
      />
      <Typography variant="body2" color="textSecondary">
        We use{" "}
        <Link href="https://www.stripe.com/" target="_blank" rel="nofollow">
          Stripe
        </Link>{" "}
        as our secure payment processor. By adding your payment information, you enable your offers to run
        uninterrupted.
      </Typography>

      {/* Payment Logos */}
      <Box py={2}>
        <img src={cardImg} alt="cards" height={30} />
      </Box>

      {/* Card Details */}
      <Box my={2}>
  <Typography variant="body2">Card number</Typography>
  <Box
    sx={{
      border: "1px solid rgba(0, 0, 0, 0.23)",
      borderRadius: "4px",
      padding: "10px",
    }}
  >
    <CardNumberElement options={{ style: { base: { fontSize: "16px" } } }} />
  </Box>
</Box>

<Grid container spacing={3}>
  <Grid item xs={6}>
    <Typography variant="body2">Expiration date</Typography>
    <Box
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.23)",
        borderRadius: "4px",
        padding: "10px",
      }}
    >
      <CardExpiryElement options={{ style: { base: { fontSize: "16px" } } }} />
    </Box>
  </Grid>
  <Grid item xs={6}>
    <Typography variant="body2">CVC</Typography>
    <Box
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.23)",
        borderRadius: "4px",
        padding: "10px",
      }}
    >
      <CardCvcElement options={{ style: { base: { fontSize: "16px" } } }} />
    </Box>
  </Grid>
</Grid>

      {/* Billing Address */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Billing address
      </Typography>

      <Box my={2}>
        <FormControl fullWidth error={!!errors.name}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => <TextField {...field} label="Name" variant="outlined" />}
          />
          <FormHelperText>{errors.name?.message}</FormHelperText>
        </FormControl>
      </Box>

      <Box my={2}>
        <FormControl fullWidth error={!!errors.address}>
          <Controller
            name="address"
            control={control}
            render={({ field }) => <TextField {...field} label="Address" variant="outlined" />}
          />
          <FormHelperText>{errors.address?.message}</FormHelperText>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={6}>
          <FormControl fullWidth error={!!errors.city}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => <TextField {...field} label="City" variant="outlined" />}
            />
            <FormHelperText>{errors.city?.message}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth error={!!errors.state}>
            <Controller
              name="state"
              control={control}
              render={({ field }) => <TextField {...field} label="State" variant="outlined" />}
            />
            <FormHelperText>{errors.state?.message}</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={6}>
          <FormControl fullWidth error={!!errors.zipCode}>
            <Controller
              name="zipCode"
              control={control}
              render={({ field }) => <TextField {...field} label="Zip code" variant="outlined" />}
            />
            <FormHelperText>{errors.zipCode?.message}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth error={!!errors.country}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => <TextField {...field} label="Country" variant="outlined" />}
            />
            <FormHelperText>{errors.country?.message}</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>

      {/* Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mt: 2 }}>
        <OfferSaveActions
          onUpdate={onUpdate}
          onSave={() => onSave({})}
          saveButtonLabel={saveButtonLabel}
        />
        <Box>
        <Box display="flex" alignItems="center">
        {(() => {
    try {
      const balanceDisplay = getBalanceDisplay(
        business.accountBalance,
        'Account balance:',
        business
      );
      return (
        <Typography variant="body2" sx={{ color: balanceDisplay.color }}>
          {balanceDisplay.text}
        </Typography>
      );
    } catch (error) {      
      return (
        <Typography variant="body2" color="error">
          Unable to display account balance. Please check your account settings.
        </Typography>
      );
    }
        })()}

          <WizardBackButton onClick={handlePaymentFormBack} />
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={!stripe || posting || !autoReloadPreference.isValid}
          >
            {posting ? "Saving..." : "Next"}
          </Button>
          </Box>
        </Box>
      </Box>
          </form>
        </Box>
      )}
      {showsPaymentOptions && (
        <Box sx={{ order: 1 }}>
          <Grid container spacing={3} alignItems="stretch">
          {!business.card ? (
            <Grid item lg={6} md={8} sm={12} mt={2}>
              <Card>
                <CardContent>
                  <Typography gutterBottom variant="h6" component="h2">
                    Choose a payment method
                  </Typography>
                  <Typography variant="body2" color="textSecondary" component="p">
                    {userHasCard
                      ? 'Select credit card from your account'
                      : 'You need to add credit card to your business profile '}
                  </Typography>
                </CardContent>
                <CardActions>
                  {userHasCard ? (
                    <>
                      <Button color="primary" variant="contained" onClick={() => setShowPaymentForm(true)}>
                        Add new card
                      </Button>
                      <Button color="primary" variant="contained" onClick={() => setShowCardSelection(true)}>
                        Select card
                      </Button>
                    </>
                  ) : (
                    <Button color="primary" onClick={() => setShowPaymentForm(true)}>
                      Add a card
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ) : (
            <Grid item lg={6} md={10} sm={12} xs={12} mt={2}>
              <Card>
                <CardContent>
                  <Typography gutterBottom variant="h6" component="h2">
                    Current payment method
                  </Typography>
                  <Box pt={2} display="flex" alignItems="center">
                    <PaymentIcon fontSize="small" style={{ marginRight: 4 }} />
                    <Typography variant="body1" color="textSecondary" component="p">
                      {business.card.brand} **** **** **** {business.card.last4} - {business.card.expMonth}/{business.card.expYear}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Box display="flex" flexDirection="column" gap={1} width="100%">
                    {/* Top row - contained variant */}
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                      <Button color="primary" variant="contained" onClick={() => setShowBalancePopup(true)}>
                        Add balance
                      </Button>
                      <Button color="primary" variant="contained" onClick={() => setShowPaymentForm(true)}>
                        Add new card
                      </Button>
                      <Button color="primary" variant="contained" onClick={() => setShowCardSelection(true)}>
                        Change card
                      </Button>
                    </Box>
                    {/* Bottom row - current styling */}
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                      <Button color="primary" onClick={handleEnterPromoClick}>
                        Enter promo code
                      </Button>
                      <AutoReloadButton
                        business={business}
                        onClick={() => setShowReloadPopup(true)}
                      />
                      <Button
                        sx={{ color: 'error.main' }}
                        onClick={() => setConfirmDelete(true)}
                      >
                        Delete card
                      </Button>
                    </Box>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          )}
    {showCardSelection && (
              <AddCard
                businessId={business.businessId}
                cards={userCards}
                card={get(business, 'card.id')}
                onClose={() => setShowCardSelection(false)}
              />
            )}
    {showBalancePopup && (
      <AddBalance
        businessId={business.businessId}
        onClose={() => setShowBalancePopup(false)}
        onFundSuccess={() => setActiveStep(5)}
      />
    )}
    {showPromoPopup && (
      <AddPromo
        businessId={business.businessId}
        onClose={() => setShowPromoPopup(false)}
      />
    )}
    {showReloadPopup && (
      <SetReload
        businessId={business.businessId}
        onClose={() => setShowReloadPopup(false)}
      />
    )}
    {confirmDelete && (
      <ConfirmationDialog
        isOpen
        title="Delete card"
        body="Are you sure you want to delete your card?"
        successButtonLabel="Delete"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => handleDeleteCard(userCards[0].id)}
      />
    )}
          </Grid>
          {!showPaymentForm && (
            <Box mt={2} mb={1} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          
          <OfferSaveActions
            onUpdate={onUpdate}
            onSave={() => onSave({})}
            saveButtonLabel={saveButtonLabel}
          />
        <Box display="flex" alignItems="center">

        {(() => {
  try {
    const balanceDisplay = getBalanceDisplay(
      business.accountBalance,
      'Account balance:',
      business
    );
    return (
      <Typography variant="body2" sx={{ color: balanceDisplay.color }}>
        {balanceDisplay.text}
      </Typography>
    );
  } catch (error) {
    return (
      <Typography variant="body2" color="error">
        Unable to display account balance. Please check your account settings.
      </Typography>
    );
  }
})()}


          <WizardBackButton onClick={back} />
          <Button variant="contained" color="primary" onClick={() => setActiveStep((prev) => prev + 1)}>
            Next
          </Button>
        </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default OfferPayment;
